import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import type { AppRole } from "./types";

/**
 * Governed decision authority.
 *
 * Deterministic evaluation of whether a signed-in person's roles carry the
 * authority for a decision on a record, given the configured thresholds. It is
 * advisory infrastructure in Package #1: it is not yet wired into the
 * recommendation Accept / Modify / Reject controls, so the current
 * human-in-the-loop experience is unchanged.
 */

export interface AuthorityRule {
  key: string;
  decision_type: string;
  required_role: AppRole;
  escalate_role: AppRole | null;
  exposure_threshold: number;
  risk_threshold: number;
  requires_second_approval: boolean;
}

const ROLE_RANK: Record<AppRole, number> = {
  viewer: 0,
  supplier_quality: 1,
  manufacturing_engineer: 1,
  quality_engineer: 2,
  quality_director: 3,
  admin: 4,
};

export interface AuthorityVerdict {
  authorised: boolean;
  matchedRuleKey: string | null;
  requiredRole: AppRole | null;
  requiresSecondApproval: boolean;
  escalateTo: AppRole | null;
  reason: string;
}

export function evaluateAuthority(args: {
  decisionType: string;
  record: Pick<ProblemRecord, "riskScore" | "financial">;
  roles: AppRole[];
  rules: AuthorityRule[];
  /**
   * When set, authority is membership in one of these roles rather than rank.
   * Used for sign-offs that belong to a discipline (e.g. Manufacturing
   * Engineering), where a higher-ranked role must not stand in.
   */
  exactRoles?: AppRole[];
}): AuthorityVerdict {
  const holds = (required: AppRole) =>
    args.exactRoles
      ? args.roles.some((r) => args.exactRoles!.includes(r))
      : args.roles.reduce((max, r) => Math.max(max, ROLE_RANK[r] ?? -1), -1) >= ROLE_RANK[required];
  const exposure = args.record.financial?.exposure ?? 0;
  const risk = args.record.riskScore ?? 0;

  const candidates = args.rules
    .filter((r) => r.decision_type === args.decisionType)
    .filter((r) => exposure >= Number(r.exposure_threshold) && risk >= Number(r.risk_threshold))
    .sort(
      (a, b) =>
        Number(b.exposure_threshold) - Number(a.exposure_threshold) ||
        Number(b.risk_threshold) - Number(a.risk_threshold),
    );

  const rule = candidates[0];
  if (!rule) {
    return {
      authorised: holds("quality_engineer"),
      matchedRuleKey: null,
      requiredRole: "quality_engineer",
      requiresSecondApproval: false,
      escalateTo: null,
      reason: "No specific authority rule matched; default engineer authority applied.",
    };
  }

  const authorised = holds(rule.required_role);

  return {
    authorised,
    matchedRuleKey: rule.key,
    requiredRole: rule.required_role,
    requiresSecondApproval: rule.requires_second_approval,
    escalateTo: rule.escalate_role,
    reason: authorised
      ? `Authority satisfied under rule ${rule.key}.`
      : `Rule ${rule.key} requires ${rule.required_role}.`,
  };
}
